import {
  Expandable,
  Slot,
  SlotListResponse,
  User,
} from '@allara-health/source-health-client'
import { CloseIcon } from '@chakra-ui/icons'
import {
  Button,
  ChakraProvider,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react'
import { ChakraPrimaryButton } from 'components/Button'
import CancelAppointmentModal from 'components/Scheduling/CancelAppointmentModal'
import ProviderDetails from 'components/Scheduling/ProviderDetails'
import TimingDetails from 'components/Scheduling/TimingDetails'
import LoadingState from 'components/Spinner'
import { RoutePaths } from 'containers/Core/Routes'
import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import { useSourceContext } from 'context/SourceContext/SourceProvider'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import withAccount, { WithAccountProps } from 'hoc/withAccount'
import useSourcePrecheck from 'hooks/useSourcePrecheck'
import useMember from 'hooks/useUser/useMember'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import {
  AppointmentBookAvailabilityShowEventProps,
  AppointmentBookSlotSelectedEventProps,
  AppointmentBookStartEventProps,
  AppointmentModifyEventProps,
  BookingEventSource,
  ProviderId,
} from 'utils/amplitude.types'
import { Amplitude } from 'utils/amplitude.utils'
import { isWithin24Hours } from 'utils/appointments.utils'
import { useIsInsurancePatient } from 'utils/program.utils'
import Calendar, { ControlHeader } from '../../components/Scheduling/Calendar'
import {
  EnhancedProvider,
  PENALTY_SCORE_MAP,
  ProviderTag,
  ProviderTagType,
} from './types'
import {
  useProviderMatchingEligibility,
  useProviderMatchingData,
} from 'hooks/useProviderMatching'
import { useProviderTimeSlots } from 'hooks/useProviderTimeSlots'
import { hasAvailableSlots } from 'hooks/useProviderTimeSlots/utils'
import './styles.scss'
import { useFlags } from 'launchdarkly-react-client-sdk'

const BookScreen: React.FC<
  WithAccountProps & {
    setDaysSinceNearestSlot: (days: number) => void
    firstAvailabilityForBookingSession: string | null
    setFirstAvailabilityForBookingSession: (date: string) => void
    targetDate: Date
  }
> = ({
  user,
  setDaysSinceNearestSlot,
  firstAvailabilityForBookingSession,
  setFirstAvailabilityForBookingSession,
  targetDate,
}) => {
  const history = useHistory()
  const {
    result: precheckResult,
    isLoading: isPrecheckLoading,
  } = useSourcePrecheck()

  const [selectedDate, setSelectedDate] = useState(targetDate)

  const [
    hasSentShowAvailabilityEvent,
    setHasSentShowAvailabilityEvent,
  ] = useState(false)
  const [
    lastShowAvailabilityEvent,
    setLastShowAvailabilityEvent,
  ] = useState<AppointmentBookAvailabilityShowEventProps | null>(null)
  const [slots, setSlots] = useState<Slot[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [orderedProviders, setOrderedProviders] = useState<EnhancedProvider[]>(
    []
  )
  const [providerTagsMap, setProviderTagsMap] = useState<
    Map<string, ProviderTag[]>
  >(new Map())
  const [showUnmatchedProviders, setShowUnmatchedProviders] = useState(false)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { source } = useSourceContext()
  const { member } = useMember()
  const [
    calendarIsAllowedToAutoAdvance,
    setCalendarIsAllowedToAutoAdvance,
  ] = useState(true)

  const { smartSchedulingPenaltyFilteringEnabled } = useFlags()

  const [viewState, setViewState] = useState<
    | 'loading'
    | 'preCheckResultIsFalse'
    | 'noSlots'
    | 'slotsAvailable'
    | 'pastPlanningHorizon'
  >('loading')

  const [showCloseIcon, setShowCloseIcon] = useState(false)
  const { isOpen, onClose, onOpen } = useDisclosure()
  const isInsurancePatient = useIsInsurancePatient(user)

  const {
    amplitudeBookingMetadata,
    appointment,
    appointmentType,
    isReschedule,
    rebookingFlowData,
  } = useSchedulingContext()

  const latestEnd = new Date()
  latestEnd.setDate(
    latestEnd.getDate() + (appointmentType?.planning_horizon ?? 0)
  )

  const onClickDashboardButton = () => {
    history.push(RoutePaths.DASHBOARD + `?from=scheduling`)
  }

  const onClickContactConciergeButton = () => {
    if (isInsurancePatient && !user?.stripeCustomer?.product) {
      window.location.href = `mailto:concierge@allarahealth.com?subject=${encodeURIComponent(
        'Potential patient not seeing availability'
      )}`
      return
    } else {
      history.push(RoutePaths.DASHBOARD_NEW_THREAD + '?sbj=8')
    }
  }

  // Update the view state based on the loading state and the availability of slots
  useEffect(() => {
    let timeoutId: number | null = null

    if (precheckResult?.result === false) {
      // Set a timeout to show the failure state after a delay
      timeoutId = window.setTimeout(() => {
        setViewState('preCheckResultIsFalse')
        onOpen()
      }, 5000)
    } else if (isPrecheckLoading || isLoading) {
      setViewState('loading')
    } else if (noSlotsAvailableOnDay()) {
      setViewState('noSlots')
    } else if (latestEnd < selectedDate) {
      setViewState('pastPlanningHorizon')
    } else {
      setViewState('slotsAvailable')
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [selectedDate, isLoading, precheckResult, isPrecheckLoading])

  const {
    currentScreen,
    timeZone,
    selectedSlot: contextSelectedSlot,
    provider,
    setSelectedSlot: setContextSelectedSlot,
    setProvider,
    setDateTime,
    setPenaltyScore,
    setPenaltyReason,
    nextScreen,
  } = useSchedulingContext()

  const chipState = (
    state:
      | 'active'
      | 'selected'
      | 'disabled'
      | 'disabled-with-border'
      | 'active-no-border'
  ): string => {
    switch (state) {
      case 'active':
        return 'chip chip-with-border'
      case 'selected':
        return 'chip chip-picked text-white'
      case 'disabled':
        return 'chip chip-disabled'
      case 'active-no-border':
        return 'chip chip-no-border'
      case 'disabled-with-border':
        return 'chip chip-with-border chip-disabled'
      default:
        return 'chip chip-with-border'
    }
  }

  const slotsForSelectedDate = useMemo(
    () =>
      slots?.filter((slot) => {
        const date = utcToZonedTime(slot.start_at, timeZone ?? '')
        return date.toDateString() === selectedDate.toDateString()
      }) || [],
    [slots, selectedDate, timeZone]
  )

  const daysBetweenDates = (earlierDate: Date, laterDateAsIso: string) => {
    const slotTime = new Date(laterDateAsIso)
    const earlierTime = earlierDate
    const slotDate = new Date(
      Date.UTC(
        slotTime.getUTCFullYear(),
        slotTime.getUTCMonth(),
        slotTime.getUTCDate()
      )
    )
    const earlier = new Date(
      Date.UTC(
        earlierTime.getUTCFullYear(),
        earlierTime.getUTCMonth(),
        earlierTime.getUTCDate()
      )
    )
    const differenceMs = slotDate.getTime() - earlier.getTime()
    const days = Math.floor(differenceMs / (1000 * 60 * 60 * 24))
    return Math.max(0, days)
  }

  const providersForSelectedDate = useMemo(() => {
    if (!slotsForSelectedDate?.length) {
      return
    }

    const extractUserId = (user: Expandable<User>): string => {
      return 'id' in user ? user.id : ((user as unknown) as User).object
    }

    const usersFlatMap = slotsForSelectedDate.flatMap((slot) => slot.available)
    const uniqueUsers = Array.from(
      new Map(
        usersFlatMap.map((user) => {
          return [extractUserId(user), user]
        })
      ).values()
    ) as User[]
    const usersWithAvailableSlots = uniqueUsers.map((user) => ({
      ...user,
      noPenaltySlots: slotsForSelectedDate.filter((slot) => {
        if (!slot.penalties || !smartSchedulingPenaltyFilteringEnabled) {
          return slot.available.some(
            (expandibleUser: Expandable<User>) =>
              extractUserId(expandibleUser) === user.id
          )
        }

        return slot.penalties?.some(
          (penalty) =>
            penalty.penalty_score === PENALTY_SCORE_MAP.no_penalty &&
            penalty.user_id === user.id
        )
      }),
      mediumPenaltySlots: !smartSchedulingPenaltyFilteringEnabled
        ? []
        : slotsForSelectedDate.filter((slot) => {
            return slot.penalties?.some(
              (penalty) =>
                penalty.penalty_score === PENALTY_SCORE_MAP.medium_penalty &&
                penalty.user_id === user.id
            )
          }),
      highPenaltySlots: !smartSchedulingPenaltyFilteringEnabled
        ? []
        : slotsForSelectedDate.filter((slot) => {
            return slot.penalties?.some(
              (penalty) =>
                penalty.penalty_score === PENALTY_SCORE_MAP.high_penalty &&
                penalty.user_id === user.id
            )
          }),
    }))
    return usersWithAvailableSlots
  }, [
    slotsForSelectedDate,
    selectedDate,
    smartSchedulingPenaltyFilteringEnabled,
  ])

  // Provider matching hooks
  const {
    isProviderMatchingEnabled,
    onboardingCondition,
  } = useProviderMatchingEligibility()

  const {
    conditionTagMappings,
    conditionDisplayName,
    conditionProviderMatchDescription,
    fetchProviderTags,
  } = useProviderMatchingData(onboardingCondition)

  const fetchSlots = useCallback(
    async (date: Date, isInitialFetch?: boolean) => {
      setIsLoading(true)
      setHasSentShowAvailabilityEvent(false)

      if (!appointmentType || !member?.id || !source) {
        setIsLoading(false)
        return
      }

      try {
        const startDate = startOfMonth(date)
        const response: SlotListResponse = await source.scheduling.slots.list(
          {
            start_at: startDate.toISOString(),
            end_at: endOfMonth(date).toISOString(),
            appointment_type: appointmentType.id ?? '',
            member: member?.id,
          },
          {
            expand: ['slots.available'],
          }
        )

        setSlots(response.slots)

        if (response.slots.length) {
          if (isInitialFetch) {
            const firstDateWithSlotsAfterTargetDate = response.slots.find(
              (slot) => new Date(slot.start_at) > date
            )

            setSelectedDate(
              new Date(
                firstDateWithSlotsAfterTargetDate
                  ? firstDateWithSlotsAfterTargetDate.start_at
                  : response.slots[response.slots.length - 1].start_at
              )
            )
          } else {
            const earliestSlot = response.slots.reduce((earliest, current) =>
              new Date(current.start_at) < new Date(earliest.start_at)
                ? current
                : earliest
            )

            if (earliestSlot) {
              setSelectedDate(new Date(earliestSlot.start_at))
            }
          }
        }
      } catch (error) {
      } finally {
        setIsLoading(false)
      }
    },
    [source, appointmentType, member?.id]
  )

  const firePostCancellationAmplitudeAppointmentBookEvents = useCallback(() => {
    if (!lastShowAvailabilityEvent) {
      return
    }

    const amplitudeBookStartEventProps: AppointmentBookStartEventProps = {
      appointment_id: null,
      is_reschedule: false,
      appointment_type_name: appointmentType?.name ?? '',
      source: BookingEventSource.APPOINTMENT_CANCEL_DONE,
      patient_action_id: null,
      patient_action_definition: null,
    }
    Amplitude.appointmentBookStart(amplitudeBookStartEventProps)
    Amplitude.appointmentBookAvailabilityShow({
      ...lastShowAvailabilityEvent,
      source: BookingEventSource.APPOINTMENT_CANCEL_DONE,
      patient_action_id: null,
      patient_action_definition: null,
    })
  }, [lastShowAvailabilityEvent, amplitudeBookingMetadata])

  useEffect(() => {
    if (hasSentShowAvailabilityEvent || isLoading) {
      return
    }

    const nearestSlotForSelectedDate =
      [...slotsForSelectedDate].sort(
        (a, b) =>
          new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
      )[0] ?? undefined

    let isFirstAvailability = false

    if (
      firstAvailabilityForBookingSession === null &&
      nearestSlotForSelectedDate
    ) {
      setFirstAvailabilityForBookingSession(
        nearestSlotForSelectedDate?.start_at
      )
      isFirstAvailability = true
    }

    // Calculate slot counts for each provider
    const calculateProviderSlotCounts = (provider: EnhancedProvider) => {
      const totalSlots =
        provider.noPenaltySlots.length +
        provider.mediumPenaltySlots.length +
        provider.highPenaltySlots.length

      // Initially show 6 slots (DEFAULT_SLOTS_COUNT from constants)
      const shownSlots = Math.min(6, totalSlots)
      const hiddenCtaSlots = Math.max(0, totalSlots - shownSlots)
      const withheldSlots = 0 // All slots are either shown or hidden behind CTA

      return {
        total: totalSlots,
        shown: shownSlots,
        hiddenCta: hiddenCtaSlots,
        withheld: withheldSlots,
      }
    }

    // Calculate total slot counts across all providers
    const totalSlotCounts = providersForSelectedDate?.reduce(
      (acc, provider) => {
        const counts = calculateProviderSlotCounts(provider)
        return {
          total: acc.total + counts.total,
          shown: acc.shown + counts.shown,
          hiddenCta: acc.hiddenCta + counts.hiddenCta,
          withheld: acc.withheld + counts.withheld,
        }
      },
      { total: 0, shown: 0, hiddenCta: 0, withheld: 0 }
    ) ?? { total: 0, shown: 0, hiddenCta: 0, withheld: 0 }

    // Create availableProvidersAndSlots with just time strings
    const availableProvidersAndSlots = providersForSelectedDate?.reduce(
      (acc, provider) => ({
        ...acc,
        [provider.id as ProviderId]: [
          ...provider.noPenaltySlots,
          ...provider.mediumPenaltySlots,
          ...provider.highPenaltySlots,
        ].map((slot) => slot.start_at),
      }),
      {} as Record<ProviderId, string[]>
    )

    // Create availableProvidersAndSlotCounts with penalty score groupings
    const availableProvidersAndSlotCounts = providersForSelectedDate?.reduce(
      (acc, provider) => {
        const penaltyCounts = [
          {
            penalty_score: PENALTY_SCORE_MAP.no_penalty,
            slots: provider.noPenaltySlots.length,
          },
          {
            penalty_score: PENALTY_SCORE_MAP.medium_penalty,
            slots: provider.mediumPenaltySlots.length,
          },
          {
            penalty_score: PENALTY_SCORE_MAP.high_penalty,
            slots: provider.highPenaltySlots.length,
          },
        ].filter((count) => count.slots > 0) // Only include penalty levels that have slots

        return {
          ...acc,
          [provider.id as ProviderId]: penaltyCounts,
        }
      },
      {} as Record<ProviderId, Array<{ penalty_score: number; slots: number }>>
    )

    setHasSentShowAvailabilityEvent(true)
    const amplitudeEventProps: AppointmentBookAvailabilityShowEventProps = {
      appointment_id: appointment?.id ?? '',
      appointment_type_name: appointmentType?.name ?? '',
      is_reschedule: isReschedule ?? false,
      slots_date: selectedDate.toISOString().split('T')[0],
      nearest_slot_time: nearestSlotForSelectedDate?.start_at ?? '',
      available_slots_cnt: slotsForSelectedDate?.length ?? 0,
      available_slot_times:
        slotsForSelectedDate?.map((slot) => slot.start_at) ?? [],
      available_providers_cnt: providersForSelectedDate?.length ?? 0,
      available_providers:
        providersForSelectedDate?.map(
          (provider) => provider.id as ProviderId
        ) ?? [],
      available_providers_and_slots: JSON.stringify(availableProvidersAndSlots),
      available_providers_and_slot_counts: JSON.stringify(
        availableProvidersAndSlotCounts
      ),
      current_month_available_slots_cnt: slots?.length ?? 0,
      current_month_available_slot_dates_cnt: datesFromSlots.length,
      state: user.waitlistUser.state.code,
      carrier: user.waitlistUser.insuranceProvider ?? '',
      is_first_availability: isFirstAvailability,
      days_to_nearest_slot: daysBetweenDates(
        new Date(),
        nearestSlotForSelectedDate?.start_at ?? ''
      ),
      no_penalty_slots_cnt:
        providersForSelectedDate?.reduce(
          (acc, provider) => acc + provider.noPenaltySlots.length,
          0
        ) ?? 0,
      medium_penalty_slots_cnt:
        providersForSelectedDate?.reduce(
          (acc, provider) => acc + provider.mediumPenaltySlots.length,
          0
        ) ?? 0,
      high_penalty_slots_cnt:
        providersForSelectedDate?.reduce(
          (acc, provider) => acc + provider.highPenaltySlots.length,
          0
        ) ?? 0,
      shown_slots_cnt: totalSlotCounts.shown,
      hidden_cta_slots_cnt: totalSlotCounts.hiddenCta,
      withheld_slots_cnt: totalSlotCounts.withheld,
      ...amplitudeBookingMetadata,
    }
    setLastShowAvailabilityEvent(amplitudeEventProps)
    Amplitude.appointmentBookAvailabilityShow(amplitudeEventProps)
  }, [hasSentShowAvailabilityEvent, providersForSelectedDate, isLoading])

  useEffect(() => {
    if (!isProviderMatchingEnabled) {
      return
    }

    const orderProvidersByTags = async () => {
      if (!providersForSelectedDate) {
        return
      }

      const providerTags = await fetchProviderTags(
        providersForSelectedDate.map((provider) => provider.id)
      )
      const conditionTagMappingsData = conditionTagMappings

      // Create a map of provider IDs to their tags for quick lookup
      const newProviderTagsMap = new Map<string, ProviderTag[]>()
      providerTags.forEach((providerTag) => {
        newProviderTagsMap.set(providerTag.provider_id, providerTag.tags)
      })
      setProviderTagsMap(newProviderTagsMap)

      // Create a set of specialty tags that match the condition for quick lookup
      const matchingSpecialtyTags = new Set<string>()

      if (conditionTagMappingsData) {
        const specialtyMappings = conditionTagMappingsData.tag_mappings.filter(
          (mapping) => mapping.tag_type === ProviderTagType.SPECIALTY
        )

        specialtyMappings.forEach((mapping) => {
          matchingSpecialtyTags.add(mapping.tag_id)
        })
      }

      const matchedProviders: EnhancedProvider[] = []
      const unmatchedProviders: EnhancedProvider[] = []

      providersForSelectedDate.forEach((provider) => {
        const providerTags = newProviderTagsMap.get(provider.id) || []

        const hasMatchingSpecialty = providerTags.some(
          (tag) =>
            tag.type === ProviderTagType.SPECIALTY &&
            matchingSpecialtyTags.has(tag.id)
        )

        if (hasMatchingSpecialty) {
          matchedProviders.push(provider)
        } else {
          unmatchedProviders.push(provider)
        }
      })

      const orderedProvidersList = [...matchedProviders, ...unmatchedProviders]
      setOrderedProviders(orderedProvidersList)
    }

    orderProvidersByTags()
  }, [
    providersForSelectedDate,
    fetchProviderTags,
    conditionTagMappings,
    isProviderMatchingEnabled,
  ])

  useEffect(() => {
    if (appointmentType && member?.id && source) {
      fetchSlots(selectedDate, true)
    }
  }, [member?.id, appointmentType?.id, source])

  // Show close icon 20 seconds after mount if isReschedule
  useEffect(() => {
    if (rebookingFlowData) {
      const timer = setTimeout(() => {
        setShowCloseIcon(true)
      }, 20000)

      return () => clearTimeout(timer)
    }
  }, [rebookingFlowData])

  const noSlotsAvailableOnDay = (): boolean => {
    if (selectedDate < latestEnd) {
      const todaySlotsLength = slotsForSelectedDate.length
      return todaySlotsLength === 0
    }

    return false
  }

  const pickDate = (date: Date) => {
    setSelectedDate(date)
    setHasSentShowAvailabilityEvent(false)

    if (date.getMonth() !== selectedDate.getMonth()) {
      fetchSlots(date)
    }
  }

  const changeCalendar = (month: 1 | -1) => {
    const newMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + month,
      1
    )
    pickDate(newMonth)
  }

  useEffect(() => {
    if (
      calendarIsAllowedToAutoAdvance &&
      !isLoading &&
      slots.length === 0 &&
      selectedDate.getMonth() === new Date().getMonth()
    ) {
      changeCalendar(1)
      setCalendarIsAllowedToAutoAdvance(false)
    }
  }, [slots, isLoading])

  const pickSlot = (slot: Slot, provider: EnhancedProvider) => {
    setProvider(provider)
    setDateTime(new Date(slot.start_at))
    setContextSelectedSlot(slot)
    const daysSinceNearestSlot = daysBetweenDates(
      new Date(firstAvailabilityForBookingSession ?? ''),
      slot.start_at
    )
    setDaysSinceNearestSlot(daysSinceNearestSlot)
    const penalty = slot.penalties?.find(
      (penalty) => penalty.user_id === provider.id
    )
    const penaltyScore = penalty?.penalty_score ?? 0
    const penaltyReason = penalty?.penalty_reason ?? ''
    const amplitudeEventProps: AppointmentBookSlotSelectedEventProps = {
      appointment_id: appointment?.id ?? '',
      appointment_type_name: appointmentType?.name ?? '',
      is_reschedule: isReschedule ?? false,
      slot_time: slot.start_at,
      provider_id: provider.id,
      days_since_nearest_slot: daysSinceNearestSlot,
      state: user.waitlistUser.state.code,
      carrier: user.waitlistUser.insuranceProvider ?? '',
      penalty_score: penaltyScore,
      ...amplitudeBookingMetadata,
    }
    setPenaltyScore(penaltyScore)
    setPenaltyReason(penaltyReason)
    Amplitude.appointmentBookSlotSelected(amplitudeEventProps)
    nextScreen()
  }

  // Provider time slots hook
  const { renderProviderTimeSlots } = useProviderTimeSlots({
    selectedDate,
    timeZone,
    providersForSelectedDate,
    onSlotClick: pickSlot,
    chipState,
  })

  const datesFromSlots: string[] = useMemo(() => {
    const dates = Array.from(
      new Set(
        slots?.map((slot) =>
          utcToZonedTime(slot.start_at, timeZone ?? '').toDateString()
        )
      )
    )
    return dates
  }, [slots])

  const [
    isInitialCancelAppointmentModalOpen,
    setIsInitialCancelAppointmentModalOpen,
  ] = useState<boolean>(false)

  const amplitudeEventProps: AppointmentModifyEventProps = {
    appointment_id: appointment?.id ?? '',
    appointment_type_name: appointmentType?.name ?? '',
    appointment_time: appointment?.start_at ?? '',
  }

  const onOpenCancelAppointmentModal = () => {
    Amplitude.appointmentCancelStart(amplitudeEventProps)
    setIsInitialCancelAppointmentModalOpen(true)
  }

  const cancellationModalContent = {
    listHeader: 'Each appointment can help you:',
    listItems: [
      'Get closer to reaching your goals',
      'Monitor your progress',
      'Address any new symptoms or concerns',
    ],
  }

  useEffect(() => {
    setContextSelectedSlot(contextSelectedSlot)
    setProvider(provider)
    setSelectedDate(
      utcToZonedTime(
        contextSelectedSlot?.start_at ?? new Date(),
        timeZone ?? ''
      )
    )
  }, [currentScreen])

  const isAppointmentModifiable = useMemo(
    () => (appointment ? !isWithin24Hours(appointment.start_at) : false),
    [appointment]
  )

  const showCancelButton = useMemo(() => {
    return isAppointmentModifiable && isReschedule && rebookingFlowData === null
  }, [isAppointmentModifiable, isReschedule, rebookingFlowData])

  const returnToDashboard = useCallback(
    () => history.push(RoutePaths.DASHBOARD + `?from=scheduling`),
    []
  )

  const perfectMatchTag = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <span
          style={{
            backgroundColor: 'var(--allara-yellow)',
            color: 'var(--blue-dark)',
            fontSize: '10px',
            fontWeight: '600',
            padding: '2px 8px',
            borderRadius: '25px',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <span style={{ fontSize: '12px' }}>🎉</span>
          <span>Perfect Match</span>
        </span>
      </div>
    ),
    []
  )

  // Ready only when we have mappings and tags loaded and a description to show
  const isMatchingDataReady = useMemo(
    () =>
      Boolean(
        isProviderMatchingEnabled &&
          conditionTagMappings &&
          conditionProviderMatchDescription &&
          providerTagsMap &&
          providerTagsMap.size > 0
      ),
    [
      isProviderMatchingEnabled,
      conditionTagMappings,
      conditionProviderMatchDescription,
      providerTagsMap,
    ]
  )

  // Memoized function to get provider tags data for a provider
  const getProviderTagsData = useCallback(
    (providerId: string) => {
      const providerTags = providerTagsMap.get(providerId)
      return providerTags
        ? {
            provider_id: providerId,
            tags: providerTags
              .filter((tag) => tag.type === ProviderTagType.CARE_EXPERTISE)
              .slice(0, 5),
          }
        : null
    },
    [providerTagsMap]
  )

  return (
    <ChakraProvider>
      <div className="screen-container">
        {rebookingFlowData && (
          <>
            {showCloseIcon && (
              <CloseIcon
                className="cursor-pointer d-none d-lg-block position-absolute top-3 right-6 m-3 fs-1"
                onClick={returnToDashboard}
                width="18px"
                height="18px"
              />
            )}
            <h1
              style={{
                fontFamily: 'Instrument Serif',
                fontWeight: 400,
                fontSize: '32px',
              }}
            >
              Your appointment has been canceled
            </h1>
            <Flex mb={8}>
              <ProviderDetails provider={rebookingFlowData.provider} />
              <TimingDetails
                date={rebookingFlowData.dateTime}
                timeZone={rebookingFlowData.timeZone}
                appointmentType={rebookingFlowData.appointmentType}
              />
            </Flex>
          </>
        )}
        <div className="appointment d-flex flex-column">
          {isProviderMatchingEnabled ? (
            <div
              className={`provider-matching-banner ${
                isMobile ? 'mb-3' : 'mb-5'
              }`}
              style={{
                width: isMobile ? 'calc(100% - 32px)' : '680px',
                margin: 0,
              }}
            >
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '16px',
                  position: 'relative',
                  display: 'flex',
                  width: '100%',
                  border: '1px solid #FFFFFF',
                }}
              >
                {/* Dark blue left border */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    backgroundColor: '#2C3068',
                    borderTopLeftRadius: '12px',
                    borderBottomLeftRadius: '12px',
                  }}
                />
                {/* Content area */}
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px',
                    }}
                  >
                    <h2
                      style={{
                        fontFamily: 'Inter',
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '1.4',
                        color: '#2C3068',
                        margin: 0,
                      }}
                    >
                      {`Meet your ${
                        conditionDisplayName || onboardingCondition
                      } specialist`}
                    </h2>
                    {isMobile ? null : perfectMatchTag}
                  </div>
                  <div
                    style={{
                      color: '#2C3068',
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      margin: 0,
                    }}
                  >
                    <ReactMarkdown
                      components={{
                        strong: ({ children }) => (
                          <strong style={{ fontWeight: 600 }}>
                            {children}
                          </strong>
                        ),
                        p: ({ children }) => (
                          <p style={{ margin: 0 }}>{children}</p>
                        ),
                      }}
                    >
                      {isMatchingDataReady
                        ? conditionProviderMatchDescription
                        : "We're finalizing your personalized matches..."}
                    </ReactMarkdown>
                    {isMobile ? (
                      <div style={{ marginTop: '12px' }}>{perfectMatchTag}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <h1
                style={{
                  fontFamily: 'Instrument Serif',
                  fontWeight: 400,
                  fontSize: '32px',
                }}
              >
                {rebookingFlowData
                  ? 'Rebook your appointment'
                  : isReschedule
                  ? 'Reschedule your appointment'
                  : 'Schedule your appointment'}
              </h1>
              <p
                style={{
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                {isReschedule || rebookingFlowData
                  ? `Let's find a time that works ${
                      rebookingFlowData ? 'best' : 'better'
                    } for you`
                  : 'Please select a date and time for your appointment'}
              </p>
            </>
          )}
          {isMobile && showCancelButton && (
            <div className="d-flex align-items-start appointment-sub">
              <Button
                onClick={onOpenCancelAppointmentModal}
                marginTop="10px"
                marginBottom="10px"
                // on mobile only
                textDecoration="underline"
                textUnderlineOffset="3px"
                variant="link"
                color="var(--blue-dark)"
                fontWeight={600}
                fontSize="16px"
                _hover={{ textDecoration: 'underline' }}
              >
                Need to cancel instead?
              </Button>
            </div>
          )}
          <div className="d-flex align-items-start appointment-sub">
            <div>
              <ControlHeader
                previous={() => {
                  changeCalendar(-1)
                }}
                next={() => {
                  changeCalendar(1)
                }}
                title={`${format(selectedDate, 'EEEE')}, ${format(
                  selectedDate,
                  'MMMM'
                )} ${selectedDate.getDate()}`}
              />
              <Calendar
                datesFromSlot={datesFromSlots}
                date={selectedDate}
                setDate={pickDate}
                slots={datesFromSlots}
              />
              {!isMobile && showCancelButton && (
                <Button
                  onClick={onOpenCancelAppointmentModal}
                  marginTop="40px"
                  marginBottom="10px"
                  textUnderlineOffset="3px"
                  variant="link"
                  color="var(--blue-dark)"
                  fontWeight={600}
                  fontSize="16px"
                  _hover={{ textDecoration: 'underline' }}
                >
                  Cancel appointment
                </Button>
              )}
            </div>
            <div className="appointment-container-scheduling ml-md-5 ">
              {(() => {
                switch (viewState) {
                  case 'loading':
                    return <LoadingState />
                  case 'preCheckResultIsFalse':
                    return (
                      <>
                        <Modal isOpen={isOpen} onClose={onClose} isCentered>
                          <ModalOverlay />
                          <ModalContent>
                            <ModalHeader>
                              Why don&apos;t I see any available appointments?
                            </ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>
                              <Text>
                                To ensure that you don&apos;t incur out of
                                network charges, our insurance team needs to
                                confirm a few details of your plan and match you
                                with participating providers. This may take up
                                to one business day.
                              </Text>
                              <Text>
                                In the meantime, if you have any questions
                                please reach out to our Patient Concierge Team.
                              </Text>
                            </ModalBody>

                            <ModalFooter justifyContent="center" gap="1rem">
                              <ChakraPrimaryButton
                                onClick={onClickContactConciergeButton}
                              >
                                Contact Concierge
                              </ChakraPrimaryButton>
                              {!isInsurancePatient && (
                                <ChakraPrimaryButton
                                  onClick={onClickDashboardButton}
                                >
                                  Go to Dashboard
                                </ChakraPrimaryButton>
                              )}
                            </ModalFooter>
                          </ModalContent>
                        </Modal>
                      </>
                    )
                  case 'noSlots':
                    return (
                      <Stack justifyContent="center" mt={2} display="flex">
                        <Text fontSize="sm">No appointments available.</Text>
                        <Text fontSize="sm">
                          Please select another date to view available
                          appointments.
                        </Text>
                      </Stack>
                    )

                  case 'slotsAvailable':
                    return (
                      <>
                        <div className="providers-list-container">
                          {orderedProviders.length > 0 ||
                          providersForSelectedDate?.length ? (
                            <div className="d-flex flex-column">
                              {!isMatchingDataReady ? (
                                // Simple list without matching while data loads
                                <>
                                  {(orderedProviders.length > 0
                                    ? orderedProviders
                                    : providersForSelectedDate
                                  )
                                    ?.filter(hasAvailableSlots)
                                    ?.map((provider) => {
                                      const providerTagsData = getProviderTagsData(
                                        provider.id
                                      )

                                      return (
                                        <div key={provider.id}>
                                          <div className="provider-container mb-4">
                                            <ProviderDetails
                                              provider={provider}
                                              className="provider-details-book-screen"
                                              providerTags={providerTagsData}
                                            />
                                            {renderProviderTimeSlots(provider)}
                                          </div>
                                        </div>
                                      )
                                    })}
                                </>
                              ) : (
                                <>
                                  {/* Render matched providers first */}
                                  {(orderedProviders.length > 0
                                    ? orderedProviders
                                    : providersForSelectedDate
                                  )
                                    ?.filter((provider) => {
                                      // Only show matched providers in the main list
                                      const providerTags = providerTagsMap.get(
                                        provider.id
                                      )
                                      const isMatched = providerTags?.some(
                                        (tag) =>
                                          tag &&
                                          tag.type ===
                                            ProviderTagType.SPECIALTY &&
                                          conditionTagMappings?.tag_mappings?.some(
                                            (mapping) =>
                                              mapping &&
                                              mapping.tag_type ===
                                                ProviderTagType.SPECIALTY &&
                                              mapping.tag_id === tag.id
                                          )
                                      )
                                      // If no condition tag mappings exist, treat all providers as unmatched
                                      return conditionTagMappings?.tag_mappings
                                        ?.length === 0
                                        ? false
                                        : isMatched
                                    })
                                    .map((provider) => {
                                      // Find the provider tags for this provider
                                      const providerTagsData = getProviderTagsData(
                                        provider.id
                                      )
                                      return (
                                        <div key={provider.id}>
                                          <div className="provider-container mb-4">
                                            <ProviderDetails
                                              provider={provider}
                                              className="provider-details-book-screen"
                                              providerTags={providerTagsData}
                                            />
                                            {renderProviderTimeSlots(provider)}
                                          </div>
                                        </div>
                                      )
                                    })}
                                  {(() => {
                                    const matchedProviders =
                                      (orderedProviders.length > 0
                                        ? orderedProviders
                                        : providersForSelectedDate
                                      )?.filter((provider) => {
                                        // First check if provider has available slots
                                        if (!hasAvailableSlots(provider)) {
                                          return false
                                        }

                                        const providerTags = providerTagsMap.get(
                                          provider.id
                                        )
                                        const isMatched = providerTags?.some(
                                          (tag) =>
                                            tag &&
                                            tag.type ===
                                              ProviderTagType.SPECIALTY &&
                                            conditionTagMappings?.tag_mappings?.some(
                                              (mapping) =>
                                                mapping &&
                                                mapping.tag_type ===
                                                  ProviderTagType.SPECIALTY &&
                                                mapping.tag_id === tag.id
                                            )
                                        )
                                        return conditionTagMappings
                                          ?.tag_mappings?.length === 0
                                          ? false
                                          : isMatched
                                      }) || []

                                    const unmatchedProviders =
                                      (orderedProviders.length > 0
                                        ? orderedProviders
                                        : providersForSelectedDate
                                      )?.filter((provider) => {
                                        // First check if provider has available slots
                                        if (!hasAvailableSlots(provider)) {
                                          return false
                                        }

                                        const providerTags = providerTagsMap.get(
                                          provider.id
                                        )
                                        const isMatched = providerTags?.some(
                                          (tag) =>
                                            tag &&
                                            tag.type ===
                                              ProviderTagType.SPECIALTY &&
                                            conditionTagMappings?.tag_mappings?.some(
                                              (mapping) =>
                                                mapping &&
                                                mapping.tag_type ===
                                                  ProviderTagType.SPECIALTY &&
                                                mapping.tag_id === tag.id
                                            )
                                        )
                                        return conditionTagMappings
                                          ?.tag_mappings?.length === 0
                                          ? true
                                          : !isMatched
                                      }) || []

                                    // Only show toggle button if there are both matched and unmatched providers
                                    const hasMatchedProviders =
                                      matchedProviders.length > 0
                                    const hasUnmatchedProviders =
                                      unmatchedProviders.length > 0

                                    return (
                                      <>
                                        {/* Show toggle button only if there are both matched and unmatched providers */}
                                        {hasMatchedProviders &&
                                          hasUnmatchedProviders && (
                                            <Button
                                              colorScheme={'var(--blue-dark)'}
                                              variant="outline"
                                              width="full"
                                              mt={0}
                                              mb={4}
                                              fontSize="14px"
                                              fontWeight={600}
                                              onClick={() =>
                                                setShowUnmatchedProviders(
                                                  !showUnmatchedProviders
                                                )
                                              }
                                            >
                                              {showUnmatchedProviders
                                                ? 'See fewer providers'
                                                : 'See other providers'}
                                            </Button>
                                          )}

                                        {/* If no matched providers, show all providers directly */}
                                        {!hasMatchedProviders &&
                                          hasUnmatchedProviders &&
                                          unmatchedProviders.map((provider) => {
                                            const providerTagsData = getProviderTagsData(
                                              provider.id
                                            )

                                            return (
                                              <div key={provider.id}>
                                                <div className="provider-container mb-4">
                                                  <ProviderDetails
                                                    provider={provider}
                                                    className="provider-details-book-screen"
                                                    providerTags={
                                                      providerTagsData
                                                    }
                                                  />
                                                  {renderProviderTimeSlots(
                                                    provider
                                                  )}
                                                </div>
                                              </div>
                                            )
                                          })}
                                      </>
                                    )
                                  })()}

                                  {/* Render unmatched providers below the button when toggle is on */}
                                  {showUnmatchedProviders &&
                                    (orderedProviders.length > 0
                                      ? orderedProviders
                                      : providersForSelectedDate
                                    )
                                      ?.filter((provider) => {
                                        // First check if provider has available slots
                                        if (!hasAvailableSlots(provider)) {
                                          return false
                                        }

                                        // Only show unmatched providers
                                        const providerTags = providerTagsMap.get(
                                          provider.id
                                        )
                                        const isMatched = providerTags?.some(
                                          (tag) =>
                                            tag &&
                                            tag.type ===
                                              ProviderTagType.SPECIALTY &&
                                            conditionTagMappings?.tag_mappings?.some(
                                              (mapping) =>
                                                mapping &&
                                                mapping.tag_type ===
                                                  ProviderTagType.SPECIALTY &&
                                                mapping.tag_id === tag.id
                                            )
                                        )
                                        // If no condition tag mappings exist, treat all providers as unmatched
                                        return conditionTagMappings
                                          ?.tag_mappings?.length === 0
                                          ? true
                                          : !isMatched
                                      })
                                      .map((provider) => {
                                        // Find the provider tags for this provider
                                        const providerTagsData = getProviderTagsData(
                                          provider.id
                                        )

                                        return (
                                          <div key={provider.id}>
                                            <div className="provider-container mb-4">
                                              <ProviderDetails
                                                provider={provider}
                                                className="provider-details-book-screen"
                                                providerTags={providerTagsData}
                                              />
                                              {renderProviderTimeSlots(
                                                provider
                                              )}
                                            </div>
                                          </div>
                                        )
                                      })}
                                </>
                              )}
                            </div>
                          ) : (
                            <Stack
                              justifyContent="center"
                              display="flex"
                              mt={2}
                              spacing={0}
                            >
                              <Text fontSize="sm">
                                No providers available for this date.
                              </Text>
                              <Text fontSize="sm">
                                Please select another date to view available
                                appointments.
                              </Text>
                            </Stack>
                          )}
                        </div>
                      </>
                    )
                  case 'pastPlanningHorizon':
                    return (
                      <Stack
                        justifyContent="center"
                        display="flex"
                        mt={2}
                        spacing={0}
                      >
                        <Text fontSize="sm" fontWeight={600}>
                          Looking for a time that works?
                        </Text>
                        <Text fontSize="sm">
                          You&apos;ve reached the end of the booking window, but
                          we&apos;re here to help. Message our{' '}
                          <Link
                            href={RoutePaths.DASHBOARD_NEW_THREAD + '?sbj=8'}
                            color={'var(--blue-light)'}
                          >
                            Concierge team
                          </Link>
                          , or give us a call at 
                          <Link
                            href={'tel:(201) 564-5109'}
                            color={'var(--blue-light)'}
                          >
                            (201) 564-5109
                          </Link>
                          . We Team can help get you scheduled at a time that
                          works for you.
                        </Text>
                      </Stack>
                    )
                  default:
                    return <LoadingState />
                }
              })()}
            </div>
          </div>
        </div>
        <CancelAppointmentModal
          open={isInitialCancelAppointmentModalOpen}
          handleClose={() => setIsInitialCancelAppointmentModalOpen(false)}
          headerText="Are you sure you want to cancel?"
          content={cancellationModalContent}
          onCancel={firePostCancellationAmplitudeAppointmentBookEvents}
        />
      </div>
    </ChakraProvider>
  )
}

export default memo(withAccount(BookScreen))
